import {Button, Tab as MuiTab, Tabs, Typography, makeStyles} from '@material-ui/core';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';
import { themeColors } from 'Theme';
import React, {useState} from 'react';

const useTabStyles = makeStyles(() => ({
  indicator: {
    backgroundColor: themeColors.blue_400,
  },
  tabRoot: {
    textTransform: 'capitalize',
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: '1.2rem',
    color: themeColors.gray_700,
  },
  activeTab: {
    color: themeColors.blue_400,
    background: themeColors.primaryWhite,
  },
  tabsContainer: {
    padding: '0 2rem',
  },
  tabContainerRoot: {
    marginBottom: '2rem',
    borderBottom: '1px solid #CCCCCC',
  },
  tabPanel: {
    padding: '0.5rem 2rem',
    minHeight: '22rem',
  },
  navigationNext: {
    justifySelf: 'flex-end',
  },
  navigationBack: {
    justifySelf: 'flex-start',
  },
  navContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '1.5rem',
    justifyContent: 'space-between',
    '& > div > button': {
      fontWeight: 600,
    },
  },
}));

type TabItem = {
  title: string;
  content: React.ReactNode;
};

type TabProps = {
  tabs: TabItem[];
  ariaLabel?: string;
  withBottomNavigation?: boolean;
};

export const TabView = ({tabs, ariaLabel, withBottomNavigation}: TabProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const classes = useTabStyles();

  return (
    <div>
      <div className={classes.tabsContainer}>
        <Tabs
          value={selectedTab}
          onChange={(_e, v) => setSelectedTab(v)}
          aria-label={ariaLabel}
          scrollButtons="auto"
          variant="scrollable"
          classes={{
            indicator: classes.indicator,
            root: classes.tabContainerRoot,
          }}
        >
          {tabs.map((tab, index) => (
            <MuiTab
              label={tab.title}
              id={`tab-${index}`}
              aria-controls={`tab-panel-${index}`}
              key={`tab-panel-${toKebabCase(tab.title)}`}
              classes={{selected: classes.activeTab, root: classes.tabRoot}}
            />
          ))}
        </Tabs>
      </div>

      {tabs.map((tab, index) => (
        <div
          role="tabpanel"
          hidden={selectedTab !== index}
          id={`tab-panel-${index}`}
          aria-labelledby={`tab-${index}`}
          className={classes.tabPanel}
          key={tab.title}
        >
          {selectedTab === index && (
            <>
              <div>
                <Typography variant="body1">{tab.content}</Typography>
              </div>
              {withBottomNavigation && (
                <div className={classes.navContainer}>
                  <div className={classes.navigationBack}>
                    {index >= 1 && index <= tabs?.length - 1 && (
                      <Button color="primary" onClick={() => setSelectedTab(index - 1)}>
                        Back To: {tabs[index - 1].title}
                      </Button>
                    )}
                  </div>
                  <div className={classes.navigationNext}>
                    {index >= 0 && index < tabs?.length - 1 && (
                      <Button color="primary" onClick={() => setSelectedTab(index + 1)}>
                        Next To: {tabs[index + 1]?.title}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
};
